import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutInfo, faqs } from '@/mocks/data';

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Header */}
        <section className="w-full px-4 md:px-6 py-12 bg-background-100 border-b border-background-200/70">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="text-center mt-6">
              <div className="w-16 h-16 rounded-2xl bg-primary-500 flex items-center justify-center mx-auto mb-4">
                <i className="ri-play-fill text-background-50 text-3xl"></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                关于{aboutInfo.brandName}
              </h1>
              <p className="text-base text-foreground-500 mt-3 max-w-2xl mx-auto">
                {aboutInfo.slogan}
              </p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50" aria-label="品牌介绍">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              <div className="lg:col-span-3">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {aboutInfo.introduction}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="lg:col-span-2">
                <div className="grid grid-cols-2 gap-4">
                  {aboutInfo.achievements.map((ach) => (
                    <div
                      key={ach.label}
                      className="p-4 rounded-lg bg-background-100 border border-background-200/70 text-center"
                    >
                      <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{ach.number}</div>
                      <div className="text-xs text-foreground-500 mt-1">{ach.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100" aria-label="核心理念">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">我们的理念</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutInfo.values.map((val) => (
                <div key={val.title} className="p-5 rounded-lg bg-background-50 border border-background-200/70 flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{val.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{val.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50" aria-label="用户口碑">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">用户口碑</h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">来自真实用户的好评</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutInfo.testimonials.map((t) => (
                <div key={t.name} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-600">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100" aria-label="常见问题">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">常见问题</h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-900 select-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50" aria-label="联系我们">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">电子邮件</p>
                <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
              </div>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-primary-500 text-lg"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">客服电话</p>
                <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
              </div>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                </div>
                <p className="text-xs text-foreground-400 mb-1">公司地址</p>
                <p className="text-xs font-medium text-foreground-900 leading-relaxed">{aboutInfo.contact.address}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": aboutInfo.brandName,
            "url": "https://www.xiaolanvideo.com/",
            "description": aboutInfo.introduction,
            "foundingDate": aboutInfo.founded,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": aboutInfo.contact.address,
              "addressLocality": "北京",
              "addressCountry": "CN",
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": aboutInfo.contact.phone,
              "email": aboutInfo.contact.email,
              "contactType": "customer service",
            },
          }),
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          }),
        }} />
      </main>

      <Footer />
    </>
  );
}